import { createI18n, type I18n } from 'vue-i18n'
import { setDatetimeLocale } from '~/lib/datetime'
import { setTranslator } from '~/lib/translate'
import { API_URL } from '~/utils/constants'
import { AUTH_MESSAGES } from '~/boot/plugins/auth-messages'

const SUPPORTED_LOCALES = ['en', 'nl'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
const DEFAULT_LOCALE: SupportedLocale = 'nl'

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as SupportedLocale)
}

let i18nInstance: I18n | null = null

const LOCAL_MESSAGES: Record<SupportedLocale, Record<string, string>> = {
  en: {
    ready_to_conclude_title: 'Ready to conclude',
    ready_to_conclude_hint: 'All tasks are resolved. You can conclude this activity now.',
    'Next step': 'Next step',
    '{done} of {total} tasks': '{done} of {total} tasks',
    'Conclude & bill': 'Conclude & bill',
    'Invoiced to {name}': 'Invoiced to {name}',
    'Invoice level': 'Invoice level',
    Client: 'Client',
    Employer: 'Employer',
    Rate: 'Rate',
    Duration: 'Duration',
    Quantity: 'Quantity',
    Subtotal: 'Subtotal',
    VAT: 'VAT',
    Total: 'Total',
    minutes: 'minutes',
    hour: 'hour',
    'Billed hourly': 'Billed hourly',
    'Billed per piece': 'Billed per piece',
    '1 piece': '1 piece',
    'Set by contract': 'Set by contract',
    'the client': 'the client',
    "Concluding creates an invoice line for {name}. This can't be undone.":
      "Concluding creates an invoice line for {name}. This can't be undone.",
    'Billing details are unavailable for this activity.':
      'Billing details are unavailable for this activity.',
    'The billing rate for this activity is €0. Please verify the contract.':
      'The billing rate for this activity is €0. Please verify the contract.',
    'Please review the following before billing:': 'Please review the following before billing:',
    'Could not load billing details. Please try again.':
      'Could not load billing details. Please try again.',
    'Activity concluded successfully': 'Activity concluded successfully',
    'Something went wrong': 'Something went wrong',
    'Report generation': 'Report generation',
    'Generate report': 'Generate report',
    'Regenerate report': 'Regenerate report',
    'Reports ready': 'Reports ready',
    'Approve all mandatory forms to generate the report.':
      'Approve all mandatory forms to generate the report.',
    'Generating the report…': 'Generating the report…',
    'The generated reports are listed below and can be edited.':
      'The generated reports are listed below and can be edited.',
    'Report generated': 'Report generated',
    'Report generation failed': 'Report generation failed',
    'Regenerating replaces the current reports. Any manual edits will be overwritten.':
      'Regenerating replaces the current reports. Any manual edits will be overwritten.',
    'Optional notes to steer the report…': 'Optional notes to steer the report…',
    'Medical Contacts': 'Medical Contacts',
    'General Practitioner': 'General Practitioner',
    Specialists: 'Specialists',
    Specialist: 'Specialist',
    GP: 'GP',
    'Add GP': 'Add GP',
    'Edit GP': 'Edit GP',
    'Add specialist': 'Add specialist',
    'Edit specialist': 'Edit specialist',
    'Practice name': 'Practice name',
    Specialism: 'Specialism',
    'Select specialism': 'Select specialism',
    'Enter specialism': 'Enter specialism',
    Other: 'Other',
    'AGB-code': 'AGB-code',
    'BIG-registration number': 'BIG-registration number',
    'First name': 'First name',
    'Last name': 'Last name',
    Initials: 'Initials',
    Prefix: 'Prefix',
    'House number': 'House number',
    'Postal code': 'Postal code',
    Street: 'Street',
    City: 'City',
    State: 'State',
    Country: 'Country',
    'Select Country': 'Select Country',
    Notes: 'Notes',
    Address: 'Address',
    Phone: 'Phone',
    Email: 'Email',
    Loading: 'Loading',
    Save: 'Save',
    Cancel: 'Cancel',
    Delete: 'Delete',
    Edit: 'Edit',
    'Delete medical contact': 'Delete medical contact',
    'Are you sure you want to delete this medical contact? This action cannot be undone.':
      'Are you sure you want to delete this medical contact? This action cannot be undone.',
    'Medical contact created': 'Medical contact created',
    'Medical contact updated': 'Medical contact updated',
    'Medical contact deleted': 'Medical contact deleted',
    'Failed to save medical contact': 'Failed to save medical contact',
    'Failed to delete medical contact': 'Failed to delete medical contact',
    'Failed to load medical contacts': 'Failed to load medical contacts',
    'Failed to load specialisms': 'Failed to load specialisms',
    'This field is required.': 'This field is required.',
    'The email field is required': 'The email field is required',
    'The password field is required': 'The password field is required',
    'Must be a valid email': 'Must be a valid email',
    'No general practitioner registered for this person.':
      'No general practitioner registered for this person.',
    'No specialists registered for this person.': 'No specialists registered for this person.',
    'Legal Hold': 'Legal Hold',
    'This person could not be found.': 'This person could not be found.',
    'On Time': 'On Time',
    'Due Soon': 'Due Soon',
    Overdue: 'Overdue',
    Concluded: 'Concluded',
    Language: 'Language',
    'Switch language to Dutch': 'Switch language to Dutch',
    'Switch language to English': 'Switch language to English',
    'Sign in to {brand}': 'Sign in to {brand}',
    'Use your professional account to continue.': 'Use your professional account to continue.',
    'Absence cases, appointments and medical records in one place.':
      'Absence cases, appointments and medical records in one place.',
    'The professional portal for occupational health teams.':
      'The professional portal for occupational health teams.',
    'Type your 6 digit security code': 'Type your 6 digit security code',
    'Not provided': 'Not provided',
    'Select a person': 'Select a person',
    'Their profile, employments, documents and recent activity appear here.':
      'Their profile, employments, documents and recent activity appear here.',
    'Select a client': 'Select a client',
    'Their details, employers and recent activity appear here.':
      'Their details, employers and recent activity appear here.',
    'Select an employer': 'Select an employer',
    'Their details, divisions and recent activity appear here.':
      'Their details, divisions and recent activity appear here.',
    'Select a division': 'Select a division',
    'Its departments, people and recent activity appear here.':
      'Its departments, people and recent activity appear here.',
    'Select a department': 'Select a department',
    'Its people and recent activity appear here.': 'Its people and recent activity appear here.',
    'Select a document': 'Select a document',
    'Document not available': 'Document not available',
    'This document is not available.': 'This document is not available.',
    'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.':
      'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.',
    'Failed to load the document. Please try again.':
      'Failed to load the document. Please try again.',
    'A preview, its version history and signature status appear here.':
      'A preview, its version history and signature status appear here.',
    'Select a task': 'Select a task',
    'Its questionnaire, activity and status appear here.':
      'Its questionnaire, activity and status appear here.',
    'Select an activity': 'Select an activity',
    'Its appointment, tasks and billing appear here.':
      'Its appointment, tasks and billing appear here.',
    'Select a signature': 'Select a signature',
    'A preview, its role and where it is used appear here.':
      'A preview, its role and where it is used appear here.',
    'Select a ticket': 'Select a ticket',
    'The conversation, attachments and status appear here.':
      'The conversation, attachments and status appear here.',
    'Select a professional': 'Select a professional',
    'Their calendar and availability appear here.': 'Their calendar and availability appear here.',
    'No matches': 'No matches',
    'No record matches the current search or filters. Try widening them.':
      'No record matches the current search or filters. Try widening them.',
    'Nothing here yet': 'Nothing here yet',
    'Records added to this list will appear here.': 'Records added to this list will appear here.',
    search_placeholder_fields: 'Search {entity} by {fields}…',
    search_placeholder_plain: 'Search {entity}…',
    search_placeholder_default: 'Search…',
    search_scope_empty: 'Search looks at {fields}.',
    search_field_conjunction: 'or',
    search_entity_persons: 'persons',
    search_entity_clients: 'clients',
    search_entity_employers: 'employers',
    search_entity_divisions: 'divisions',
    search_entity_departments: 'departments',
    search_entity_documents: 'documents',
    search_entity_signatures: 'signatures',
    search_entity_tickets: 'tickets',
    search_entity_tasks: 'tasks',
    search_entity_professionals: 'professionals',
    search_entity_notifications: 'notifications',
    search_entity_employments: 'employments',
    search_entity_file_access: 'file access',
    search_entity_the_action_list: 'the action list',
    search_entity_timeline_activities: 'timeline activities',
    search_entity_absentees: 'absentees',
    'Days in Range (this case)': 'Days in Range (this case)',
    'Days in Range (all cases)': 'Days in Range (all cases)',
    'Case Duration (days)': 'Case Duration (days)',
    'No results match your filters': 'No results match your filters',
    'Try removing a filter or searching for something else.':
      'Try removing a filter or searching for something else.',
    'Absence view': 'Absence view',
    'Pick an appointment on the left to see its tasks.':
      'Pick an appointment on the left to see its tasks.',
    'Select an appointment': 'Select an appointment',
    total: 'total',
    search_field_name: 'name',
    search_field_email: 'email',
    search_field_phone: 'phone',
    search_field_code: 'code',
    search_field_type: 'type',
    search_field_description: 'description',
    search_field_notes: 'notes',
    search_field_subject: 'subject',
    search_field_activity_name: 'activity name',
    search_field_message: 'message',
    vs: 'vs',
    'No earlier period to compare with': 'No earlier period to compare with',
    'Two periods is a comparison, not a trend. A trend line needs at least three.':
      'Two periods is a comparison, not a trend. A trend line needs at least three.',
    'No data for this selection': 'No data for this selection',
    'Adjust the filters or widen the period to see results.':
      'Adjust the filters or widen the period to see results.',
    'Error downloading file': 'Error downloading file',
    'Error capturing chart image': 'Error capturing chart image',
    'Error loading SVG image': 'Error loading SVG image',
    'SVG element not found in chart container': 'SVG element not found in chart container',
    'Could not get canvas context': 'Could not get canvas context',
    'Age Distribution Analysis': 'Age Distribution Analysis',
    'Age Group': 'Age Group',
    Count: 'Count',
    Percentage: 'Percentage',
    'Percentage (%)': 'Percentage (%)',
    'Yearly Absence Analysis': 'Yearly Absence Analysis',
    Year: 'Year',
    'Absence Percentage': 'Absence Percentage',
    'Absence Percentage (%)': 'Absence Percentage (%)',
    Frequency: 'Frequency',
    Gender: 'Gender',
    Category: 'Category',
    'Diagnosis Category': 'Diagnosis Category',
    'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.':
      'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.',
    'This certificate is signed and can no longer be updated.':
      'This certificate is signed and can no longer be updated.',
    'Save to keep a draft and finish later, or Complete to submit this task.':
      'Save to keep a draft and finish later, or Complete to submit this task.',
    'Complete all forms to finish the task.': 'Complete all forms to finish the task.',
    'You can refresh the documents until a form is completed.':
      'You can refresh the documents until a form is completed.',
    'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.':
      'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.',
    'Update applies to this form only.': 'Update applies to this form only.',
    'Complete the form to download the certificate.':
      'Complete the form to download the certificate.',
    'This document is signed and is the final version.':
      'This document is signed and is the final version.',
    'A document is signed and is the final version.':
      'A document is signed and is the final version.',
    'You do not have permission to view this task.':
      'You do not have permission to view this task.',
    'This task can be viewed once it has been completed.':
      'This task can be viewed once it has been completed.',
    'One of the documents is signed and is the final version.':
      'One of the documents is signed and is the final version.',
    'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.',
    'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.',
    'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.',
    'Updating regenerates the document with the latest data. Continue?':
      'Updating regenerates the document with the latest data. Continue?',
    'Show page list': 'Show page list',
    'Hide page list': 'Hide page list',
    'Refreshing...': 'Refreshing...',
    'Refresh Document': 'Refresh Document',
    Refresh: 'Refresh',
    Download: 'Download',
    'Failed to download the certificate': 'Failed to download the certificate',
    Notifications: 'Notifications',
    new: 'new',
    Unread: 'Unread',
    More: 'More',
    'Mark all as read': 'Mark all as read',
    'All notifications marked as read': 'All notifications marked as read',
    'View details': 'View details',
    'Back to notifications': 'Back to notifications',
    'No notifications yet': 'No notifications yet',
    'View all notifications': 'View all notifications',
    Today: 'Today',
    Yesterday: 'Yesterday',
    Undated: 'Undated',
    Absence: 'Absence',
    Task: 'Task',
    Deadline: 'Deadline',
    Signaling: 'Signaling',
    Notification: 'Notification',
    'Nothing unread': 'Nothing unread',
    'Everything here has been read.': 'Everything here has been read.',
    'Updates about your cases and tasks will appear here.':
      'Updates about your cases and tasks will appear here.',
    'No notifications match your search.': 'No notifications match your search.',
    'Select a notification': 'Select a notification',
    'Its full message and the record it points to appear here.':
      'Its full message and the record it points to appear here.',
    'Open related record': 'Open related record',
    'This notification has no further details.': 'This notification has no further details.',
    Open: 'Open',
    'In Progress': 'In Progress',
    Pending: 'Pending',
    Resolved: 'Resolved',
    Closed: 'Closed',
    'No longer available': 'No longer available',
    'No access': 'No access',
    'Could not load': 'Could not load',
    'Something went wrong. Please try again.': 'Something went wrong. Please try again.',
    Retry: 'Retry',
    'This absence no longer exists — it may have been deleted.':
      'This absence no longer exists — it may have been deleted.',
    'This employment no longer exists — it may have been deleted.':
      'This employment no longer exists — it may have been deleted.',
    'This document no longer exists — it may have been deleted.':
      'This document no longer exists — it may have been deleted.',
    'This task no longer exists — it may have been deleted.':
      'This task no longer exists — it may have been deleted.',
    'You are not authorised to view this absence.': 'You are not authorised to view this absence.',
    'You are not authorised to view this employment.':
      'You are not authorised to view this employment.',
    'You are not authorised to view this document.':
      'You are not authorised to view this document.',
    'You are not authorised to view this task.': 'You are not authorised to view this task.',
    'This person no longer exists — they may have been deleted.':
      'This person no longer exists — they may have been deleted.',
    'You are not authorised to view this person.': 'You are not authorised to view this person.',
  },
  nl: {
    ready_to_conclude_title: 'Klaar om af te ronden',
    ready_to_conclude_hint: 'Alle taken zijn afgehandeld. U kunt deze activiteit nu afronden.',
    'Next step': 'Volgende stap',
    '{done} of {total} tasks': '{done} van {total} taken',
    'Conclude & bill': 'Afronden & factureren',
    'Invoiced to {name}': 'Gefactureerd aan {name}',
    'Invoice level': 'Factuurniveau',
    Client: 'Klant',
    Employer: 'Werkgever',
    Rate: 'Tarief',
    Duration: 'Duur',
    Quantity: 'Aantal',
    Subtotal: 'Subtotaal',
    VAT: 'Btw',
    Total: 'Totaal',
    minutes: 'minuten',
    hour: 'uur',
    'Billed hourly': 'Per uur gefactureerd',
    'Billed per piece': 'Per stuk gefactureerd',
    '1 piece': '1 stuk',
    'Set by contract': 'Vastgelegd in contract',
    'the client': 'de klant',
    "Concluding creates an invoice line for {name}. This can't be undone.":
      'Afronden maakt een factuurregel aan voor {name}. Dit kan niet ongedaan worden gemaakt.',
    'Billing details are unavailable for this activity.':
      'Factuurgegevens zijn niet beschikbaar voor deze activiteit.',
    'The billing rate for this activity is €0. Please verify the contract.':
      'Het factuurtarief voor deze activiteit is €0. Controleer het contract.',
    'Please review the following before billing:': 'Controleer het volgende voordat u factureert:',
    'Could not load billing details. Please try again.':
      'Kon de factuurgegevens niet laden. Probeer het opnieuw.',
    'Activity concluded successfully': 'Activiteit succesvol afgerond',
    'Something went wrong': 'Er is iets misgegaan',
    'Report generation': 'Rapportgeneratie',
    'Generate report': 'Rapport genereren',
    'Regenerate report': 'Rapport opnieuw genereren',
    'Reports ready': 'Rapporten gereed',
    'Approve all mandatory forms to generate the report.':
      'Keur alle verplichte formulieren goed om het rapport te genereren.',
    'Generating the report…': 'Rapport wordt gegenereerd…',
    'The generated reports are listed below and can be edited.':
      'De gegenereerde rapporten staan hieronder en kunnen worden bewerkt.',
    'Report generated': 'Rapport gegenereerd',
    'Report generation failed': 'Rapportgeneratie mislukt',
    'Regenerating replaces the current reports. Any manual edits will be overwritten.':
      'Opnieuw genereren vervangt de huidige rapporten. Handmatige wijzigingen gaan verloren.',
    'Optional notes to steer the report…': 'Optionele notities om het rapport te sturen…',
    'Medical Contacts': 'Medische contacten',
    'General Practitioner': 'Huisarts',
    Specialists: 'Specialisten',
    Specialist: 'Specialist',
    GP: 'Huisarts',
    'Add GP': 'Huisarts toevoegen',
    'Edit GP': 'Huisarts bewerken',
    'Add specialist': 'Specialist toevoegen',
    'Edit specialist': 'Specialist bewerken',
    'Practice name': 'Praktijknaam',
    Specialism: 'Specialisme',
    'Select specialism': 'Selecteer specialisme',
    'Enter specialism': 'Voer specialisme in',
    Other: 'Overig',
    'AGB-code': 'AGB-code',
    'BIG-registration number': 'BIG-registratienummer',
    'First name': 'Voornaam',
    'Last name': 'Achternaam',
    Initials: 'Initialen',
    Prefix: 'Voorvoegsel',
    'House number': 'Huisnummer',
    'Postal code': 'Postcode',
    Street: 'Straat',
    City: 'Plaats',
    State: 'Provincie',
    Country: 'Land',
    'Select Country': 'Selecteer land',
    Notes: 'Notities',
    Address: 'Adres',
    Phone: 'Telefoon',
    Email: 'E-mail',
    Loading: 'Laden',
    Save: 'Opslaan',
    Cancel: 'Annuleren',
    Delete: 'Verwijderen',
    Edit: 'Bewerken',
    'Delete medical contact': 'Medisch contact verwijderen',
    'Are you sure you want to delete this medical contact? This action cannot be undone.':
      'Weet u zeker dat u dit medische contact wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    'Medical contact created': 'Medisch contact aangemaakt',
    'Medical contact updated': 'Medisch contact bijgewerkt',
    'Medical contact deleted': 'Medisch contact verwijderd',
    'Failed to save medical contact': 'Opslaan van medisch contact mislukt',
    'Failed to delete medical contact': 'Verwijderen van medisch contact mislukt',
    'Failed to load medical contacts': 'Laden van medische contacten mislukt',
    'Failed to load specialisms': 'Laden van specialismen mislukt',
    'This field is required.': 'Dit veld is verplicht.',
    'The email field is required': 'Het e-mailadres is verplicht.',
    'The password field is required': 'Het wachtwoord is verplicht.',
    'Must be a valid email': 'Moet een geldig e-mailadres zijn.',
    'No general practitioner registered for this person.':
      'Geen huisarts geregistreerd voor deze persoon.',
    'No specialists registered for this person.':
      'Geen specialisten geregistreerd voor deze persoon.',
    'Legal Hold': 'Juridische bewaringsplicht',
    'This person could not be found.': 'Deze persoon kon niet worden gevonden.',
    'On Time': 'Op tijd',
    'Due Soon': 'Verloopt binnenkort',
    Overdue: 'Te laat',
    Concluded: 'Afgerond',
    Language: 'Taal',
    'Switch language to Dutch': 'Schakel taal naar Nederlands',
    'Switch language to English': 'Schakel taal naar Engels',
    'Sign in to {brand}': 'Inloggen bij {brand}',
    'Use your professional account to continue.':
      'Gebruik uw professionele account om verder te gaan.',
    'Absence cases, appointments and medical records in one place.':
      'Verzuimdossiers, afspraken en medische documenten op één plek.',
    'The professional portal for occupational health teams.':
      'Het professionele portaal voor arbodienstverleners.',
    'Type your 6 digit security code': 'Voer uw 6-cijferige beveiligingscode in',
    'Not provided': 'Niet ingevuld',
    'Select a person': 'Selecteer een persoon',
    'Their profile, employments, documents and recent activity appear here.':
      'Hier verschijnen het profiel, dienstverbanden, documenten en recente activiteit.',
    'Select a client': 'Selecteer een klant',
    'Their details, employers and recent activity appear here.':
      'Hier verschijnen de gegevens, werkgevers en recente activiteit.',
    'Select an employer': 'Selecteer een werkgever',
    'Their details, divisions and recent activity appear here.':
      'Hier verschijnen de gegevens, divisies en recente activiteit.',
    'Select a division': 'Selecteer een divisie',
    'Its departments, people and recent activity appear here.':
      'Hier verschijnen de afdelingen, personen en recente activiteit.',
    'Select a department': 'Selecteer een afdeling',
    'Its people and recent activity appear here.':
      'Hier verschijnen de personen en recente activiteit.',
    'Select a document': 'Selecteer een document',
    'Document not available': 'Document niet beschikbaar',
    'This document is not available.': 'Dit document is niet beschikbaar.',
    'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.':
      'Dit document is niet beschikbaar. Het is mogelijk verwijderd of hoort bij een taak die nog niet is voltooid.',
    'Failed to load the document. Please try again.':
      'Het document kon niet worden geladen. Probeer het opnieuw.',
    'A preview, its version history and signature status appear here.':
      'Hier verschijnen een voorbeeld, de versiegeschiedenis en de handtekeningstatus.',
    'Select a task': 'Selecteer een taak',
    'Its questionnaire, activity and status appear here.':
      'Hier verschijnen de vragenlijst, activiteit en status.',
    'Select an activity': 'Selecteer een activiteit',
    'Its appointment, tasks and billing appear here.':
      'Hier verschijnen de afspraak, taken en facturatie.',
    'Select a signature': 'Selecteer een handtekening',
    'A preview, its role and where it is used appear here.':
      'Hier verschijnen een voorbeeld, de rol en waar deze wordt gebruikt.',
    'Select a ticket': 'Selecteer een ticket',
    'The conversation, attachments and status appear here.':
      'Hier verschijnen het gesprek, bijlagen en de status.',
    'Select a professional': 'Selecteer een professional',
    'Their calendar and availability appear here.':
      'Hier verschijnen de agenda en beschikbaarheid.',
    'No matches': 'Geen resultaten',
    'No record matches the current search or filters. Try widening them.':
      'Geen enkel record voldoet aan de huidige zoekopdracht of filters. Probeer ze te verbreden.',
    'Nothing here yet': 'Nog niets',
    'Records added to this list will appear here.':
      'Records die aan deze lijst worden toegevoegd verschijnen hier.',
    search_placeholder_fields: 'Zoek {entity} op {fields}…',
    search_placeholder_plain: 'Zoek {entity}…',
    search_placeholder_default: 'Zoeken…',
    search_scope_empty: 'Er wordt gezocht op {fields}.',
    search_field_conjunction: 'of',
    search_entity_persons: 'personen',
    search_entity_clients: 'klanten',
    search_entity_employers: 'werkgevers',
    search_entity_divisions: 'divisies',
    search_entity_departments: 'afdelingen',
    search_entity_documents: 'documenten',
    search_entity_signatures: 'handtekeningen',
    search_entity_tickets: 'tickets',
    search_entity_tasks: 'taken',
    search_entity_professionals: 'professionals',
    search_entity_notifications: 'meldingen',
    search_entity_employments: 'dienstverbanden',
    search_entity_file_access: 'dossiertoegang',
    search_entity_the_action_list: 'de actielijst',
    search_entity_timeline_activities: 'tijdlijnactiviteiten',
    search_entity_absentees: 'verzuimende medewerkers',
    'Days in Range (this case)': 'Dagen in periode (dit verzuimgeval)',
    'Days in Range (all cases)': 'Dagen in periode (alle verzuimgevallen)',
    'Case Duration (days)': 'Duur verzuimgeval (dagen)',
    'No results match your filters': 'Geen resultaten voor uw filters',
    'Try removing a filter or searching for something else.':
      'Verwijder een filter of zoek op iets anders.',
    'Absence view': 'Verzuimweergave',
    'Pick an appointment on the left to see its tasks.':
      'Kies links een afspraak om de taken te zien.',
    'Select an appointment': 'Selecteer een afspraak',
    total: 'totaal',
    search_field_name: 'naam',
    search_field_email: 'e-mail',
    search_field_phone: 'telefoon',
    search_field_code: 'code',
    search_field_type: 'type',
    search_field_description: 'omschrijving',
    search_field_notes: 'notities',
    search_field_subject: 'onderwerp',
    search_field_message: 'bericht',
    search_field_activity_name: 'activiteitnaam',
    vs: 'vs',
    'No earlier period to compare with': 'Geen eerdere periode om mee te vergelijken',
    'Two periods is a comparison, not a trend. A trend line needs at least three.':
      'Twee perioden is een vergelijking, geen trend. Een trendlijn heeft er minimaal drie nodig.',
    'No data for this selection': 'Geen gegevens voor deze selectie',
    'Adjust the filters or widen the period to see results.':
      'Pas de filters aan of verruim de periode om resultaten te zien.',
    'Error downloading file': 'Fout bij het downloaden van het bestand',
    'Error capturing chart image': 'Fout bij het vastleggen van de grafiekafbeelding',
    'Error loading SVG image': 'Fout bij het laden van de SVG-afbeelding',
    'SVG element not found in chart container': 'SVG-element niet gevonden in de grafiekcontainer',
    'Could not get canvas context': 'Kan de canvascontext niet ophalen',
    'Age Distribution Analysis': 'Leeftijdsverdeling',
    'Age Group': 'Leeftijdsgroep',
    Count: 'Aantal',
    Percentage: 'Percentage',
    'Percentage (%)': 'Percentage (%)',
    'Yearly Absence Analysis': 'Verzuim per jaar',
    Year: 'Jaar',
    'Absence Percentage': 'Verzuimpercentage',
    'Absence Percentage (%)': 'Verzuimpercentage (%)',
    Frequency: 'Frequentie',
    Gender: 'Geslacht',
    Category: 'Categorie',
    'Diagnosis Category': 'Diagnosecategorie',
    'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.':
      'Dit document is ondertekend — dit is de definitieve versie en kan niet meer worden bijgewerkt. Het kan indien nodig nog door de andere partijen worden ondertekend.',
    'This certificate is signed and can no longer be updated.':
      'Dit certificaat is ondertekend en kan niet meer worden bijgewerkt.',
    'Save to keep a draft and finish later, or Complete to submit this task.':
      'Opslaan om een concept te bewaren en later af te maken, of Voltooien om deze taak in te dienen.',
    'Complete all forms to finish the task.': 'Voltooi alle formulieren om de taak af te ronden.',
    'You can refresh the documents until a form is completed.':
      'U kunt de documenten vernieuwen totdat een formulier is voltooid.',
    'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.':
      'Een formulier is voltooid, dus de documenten kunnen niet meer worden vernieuwd. Werk een formulier bij om de nieuwe waarden in de documenten te zien.',
    'Update applies to this form only.': 'Bijwerken geldt alleen voor dit formulier.',
    'Complete the form to download the certificate.':
      'Voltooi het formulier om het certificaat te downloaden.',
    'This document is signed and is the final version.':
      'Dit document is ondertekend en is de definitieve versie.',
    'A document is signed and is the final version.':
      'Een document is ondertekend en is de definitieve versie.',
    'You do not have permission to view this task.':
      'U heeft geen toestemming om deze taak te bekijken.',
    'This task can be viewed once it has been completed.':
      'Deze taak kan worden bekeken zodra deze is voltooid.',
    'One of the documents is signed and is the final version.':
      'Een van de documenten is ondertekend en is de definitieve versie.',
    'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — de formulieren en documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — het formulier en de documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — de documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'Updating regenerates the document with the latest data. Continue?':
      'Bijwerken genereert het document opnieuw met de nieuwste gegevens. Doorgaan?',
    'Show page list': 'Paginalijst tonen',
    'Hide page list': 'Paginalijst verbergen',
    'Refreshing...': 'Vernieuwen...',
    'Refresh Document': 'Document vernieuwen',
    Refresh: 'Vernieuwen',
    Download: 'Downloaden',
    'Failed to download the certificate': 'Kon het certificaat niet downloaden.',
    Notifications: 'Meldingen',
    new: 'nieuw',
    Unread: 'Ongelezen',
    More: 'Meer',
    'Mark all as read': 'Alles als gelezen markeren',
    'All notifications marked as read': 'Alle meldingen zijn als gelezen gemarkeerd',
    'View details': 'Details bekijken',
    'Back to notifications': 'Terug naar meldingen',
    'No notifications yet': 'Nog geen meldingen',
    'View all notifications': 'Alle meldingen bekijken',
    Today: 'Vandaag',
    Yesterday: 'Gisteren',
    Undated: 'Zonder datum',
    Absence: 'Verzuim',
    Task: 'Taak',
    Deadline: 'Deadline',
    Signaling: 'Signalering',
    Notification: 'Melding',
    'Nothing unread': 'Niets ongelezen',
    'Everything here has been read.': 'Alles hier is gelezen.',
    'Updates about your cases and tasks will appear here.':
      'Updates over uw dossiers en taken verschijnen hier.',
    'No notifications match your search.': 'Geen meldingen komen overeen met uw zoekopdracht.',
    'Select a notification': 'Selecteer een melding',
    'Its full message and the record it points to appear here.':
      'Het volledige bericht en het bijbehorende record verschijnen hier.',
    'Open related record': 'Bijbehorend record openen',
    'This notification has no further details.': 'Deze melding heeft geen verdere details.',
    Open: 'Open',
    'In Progress': 'In uitvoering',
    Pending: 'In behandeling',
    Resolved: 'Opgelost',
    Closed: 'Gesloten',
    'Employer/HR': 'Werkgever/HR',
    'Supervisor (Employer)': 'Leidinggevende (Werkgever)',
    'Absence Contact (Employer)': 'Verzuim contactpersoon (Werkgever)',
    'HSE Contact (Employer)': 'Arbo contactpersoon (Werkgever)',
    'Main Contact (Employer)': 'Hoofdcontactpersoon (Werkgever)',
    'Main Contact (Client)': 'Hoofdcontactpersoon (Opdrachtgever)',
    'No longer available': 'Niet meer beschikbaar',
    'No access': 'Geen toegang',
    'Could not load': 'Kon niet worden geladen',
    'Something went wrong. Please try again.': 'Er is iets misgegaan. Probeer het opnieuw.',
    Retry: 'Opnieuw proberen',
    'This absence no longer exists — it may have been deleted.':
      'Dit verzuim bestaat niet meer — het is mogelijk verwijderd.',
    'This employment no longer exists — it may have been deleted.':
      'Dit dienstverband bestaat niet meer — het is mogelijk verwijderd.',
    'This document no longer exists — it may have been deleted.':
      'Dit document bestaat niet meer — het is mogelijk verwijderd.',
    'This task no longer exists — it may have been deleted.':
      'Deze taak bestaat niet meer — deze is mogelijk verwijderd.',
    'You are not authorised to view this absence.': 'U bent niet bevoegd dit verzuim te bekijken.',
    'You are not authorised to view this employment.':
      'U bent niet bevoegd dit dienstverband te bekijken.',
    'You are not authorised to view this document.':
      'U bent niet bevoegd dit document te bekijken.',
    'You are not authorised to view this task.': 'U bent niet bevoegd deze taak te bekijken.',
    'This person no longer exists — they may have been deleted.':
      'Deze persoon bestaat niet meer — deze is mogelijk verwijderd.',
    'You are not authorised to view this person.': 'U bent niet bevoegd deze persoon te bekijken.',
  },
}

Object.assign(LOCAL_MESSAGES.en, AUTH_MESSAGES.en)
Object.assign(LOCAL_MESSAGES.nl, AUTH_MESSAGES.nl)

/**
 * Detect browser language and map to supported locale.
 * Priority: localStorage > browser language > default (nl)
 *
 * Runs at boot, before any user is known. `AuthStore.setLocale()` then layers
 * the profile locale on top, but only when the profile has a supported one, so
 * the effective post-login order is:
 * profile (when set) > localStorage > browser language > default (nl)
 */
function detectLocale(): SupportedLocale {
  const savedLocale = localStorage.getItem('locale')
  if (isSupportedLocale(savedLocale)) {
    return savedLocale
  }

  if (typeof navigator !== 'undefined') {
    const browserLang =
      navigator.language || (navigator as unknown as Record<string, string>).userLanguage
    if (browserLang) {
      const langCode = browserLang.split('-')[0].toLowerCase()
      if (isSupportedLocale(langCode)) {
        return langCode
      }
    }
  }

  return DEFAULT_LOCALE
}

function applyDocumentLang(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('lang', locale)
}

/**
 * Set locale and persist to localStorage
 */
export function setLocale(locale: SupportedLocale): void {
  if (!i18nInstance) {
    console.warn('i18n instance not available')
    return
  }

  localStorage.setItem('locale', locale)
  applyDocumentLang(locale)
  setDatetimeLocale(locale)
  const localeObj = i18nInstance.global.locale as unknown as Record<string, string> | string
  if (typeof localeObj === 'object' && localeObj !== null && 'value' in localeObj) {
    ;(localeObj as { value: string }).value = locale
  } else {
    ;(i18nInstance.global as unknown as Record<string, unknown>).locale = locale
  }
}

/**
 * Tracks whether the user made an explicit locale choice before authenticating
 * (the login-page toggle). When set, AuthStore must NOT overwrite it with the
 * stored profile locale after login — otherwise a user whose profile locale
 * differs from their login-page pick sees the language flip on login (#838).
 * Scoped to sessionStorage so it governs the current login only and is cleared
 * on logout.
 */
const EXPLICIT_LOCALE_KEY = 'locale_explicit'

export function markExplicitLocaleSelection(): void {
  try {
    sessionStorage.setItem(EXPLICIT_LOCALE_KEY, '1')
  } catch {
    // sessionStorage unavailable (private mode / SSR) — degrade silently.
  }
}

export function hasExplicitLocaleSelection(): boolean {
  try {
    return sessionStorage.getItem(EXPLICIT_LOCALE_KEY) === '1'
  } catch {
    return false
  }
}

export function clearExplicitLocaleSelection(): void {
  try {
    sessionStorage.removeItem(EXPLICIT_LOCALE_KEY)
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

/**
 * Get current locale
 */
export function getLocale(): SupportedLocale {
  if (i18nInstance) {
    const locale = i18nInstance.global.locale
    const localeValue =
      typeof locale === 'object' && locale !== null && 'value' in locale ? locale.value : locale
    return localeValue as SupportedLocale
  }
  return detectLocale()
}

/**
 * Get i18n instance
 */
export function getI18nInstance(): I18n | null {
  return i18nInstance
}

/**
 * Fetch translations from API
 */
async function fetchTranslations(locale: string) {
  try {
    console.log(`Fetching translations for locale: ${locale}`)
    const response = await fetch(`${API_URL}/locale/${locale}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const translations = await response.json()
    console.log(`Successfully fetched translations for ${locale}:`, translations)
    return translations
  } catch (error) {
    console.warn(`Error fetching translations for ${locale}:`, error)
    console.log(`Using fallback translations for ${locale}`)

    const fallbackTranslations = {
      en: {
        Save: 'Save',
        Saved: 'Saved',
        'Oops! The page you are looking for does not exist.':
          'Oops! The page you are looking for does not exist.',
        'Go to Home': 'Go to Home',
        success: 'Success',
      },
      nl: {
        Save: 'Opslaan',
        Saved: 'Opgeslagen',
        'Oops! The page you are looking for does not exist.':
          'Oeps! De pagina die u zoekt bestaat niet.',
        'Go to Home': 'Ga naar Home',
        success: 'Succes',
      },
    }
    return fallbackTranslations[locale as keyof typeof fallbackTranslations] || {}
  }
}

/**
 * Update translations for a specific locale
 */
export async function updateTranslations(locale: string) {
  if (!i18nInstance) {
    console.warn('i18n instance not available for updating translations')
    return
  }

  try {
    const translations = await fetchTranslations(locale)
    i18nInstance.global.setLocaleMessage(locale, translations)
    console.log(`Translations updated for locale: ${locale}`)
  } catch (error) {
    console.error(`Failed to update translations for ${locale}:`, error)
  }
}

/**
 * Refresh all translations
 */
export async function refreshAllTranslations() {
  if (!i18nInstance) {
    console.warn('i18n instance not available for refreshing translations')
    return
  }

  try {
    const [enTranslations, nlTranslations] = await Promise.all([
      fetchTranslations('en'),
      fetchTranslations('nl'),
    ])

    i18nInstance.global.setLocaleMessage('en', enTranslations)
    i18nInstance.global.setLocaleMessage('nl', nlTranslations)
    console.log('All translations refreshed successfully')
  } catch (error) {
    console.error('Failed to refresh translations:', error)
  }
}

/**
 * Initialize i18n with fetched translations
 */
async function initializeI18n() {
  console.log('Initializing i18n with API translations...')

  try {
    const [enTranslations, nlTranslations] = await Promise.all([
      fetchTranslations('en'),
      fetchTranslations('nl'),
    ])

    const detectedLocale = detectLocale()
    if (!localStorage.getItem('locale')) {
      localStorage.setItem('locale', detectedLocale)
    }

    i18nInstance = createI18n({
      locale: detectedLocale,
      fallbackLocale: 'en',
      messages: {
        en: { ...LOCAL_MESSAGES.en, ...enTranslations },
        nl: { ...LOCAL_MESSAGES.nl, ...nlTranslations },
      },
      legacy: false,
      // `t('Available in {minutes} min', { minutes })` renders "Available in
      // 30 min" instead of the raw "{minutes}" placeholder. Without this, a
      fallbackFormat: true,
      missingWarn: false,
      fallbackWarn: false,
    })

    applyDocumentLang(detectedLocale)
    setDatetimeLocale(detectedLocale)
    const global = i18nInstance.global as unknown as { t: (key: string) => string }
    setTranslator((key) => global.t(key))

    console.log('i18n initialized successfully')
    return i18nInstance
  } catch (error) {
    console.error('Failed to initialize i18n:', error)
    throw error
  }
}

export default initializeI18n()
